import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { AccountBalanceOutlined, Add, Check, Close } from "@mui/icons-material";

/** Los tres meses que se piden por cuenta. Mock: el flujo real los derivará de la fecha de solicitud. */
const MESES = ["Junio 2026", "Mayo 2026", "Abril 2026"];

/** Placeholder mientras no se identifica el banco a partir del PDF. */
const BANCO_SIN_IDENTIFICAR = "#8B9EB7";

/** Mock de la detección de banco: hoy siempre resuelve al mismo, sin importar el archivo. */
const BANCO_DETECTADO = { nombre: "BBVA", clabe: "012 180 •••• •••• 8421", color: "#004481" };

interface Cuenta {
  id: number;
  nombre: string | null;
  clabe: string | null;
  color: string;
  archivos: (string | null)[];
}

const nuevaCuenta = (id: number): Cuenta => ({
  id,
  nombre: null,
  clabe: null,
  color: BANCO_SIN_IDENTIFICAR,
  archivos: MESES.map(() => null),
});

interface Props {
  /** Reporta si todas las cuentas ya tienen sus tres estados, para habilitar el paso siguiente. */
  onCompleteChange: (complete: boolean) => void;
}

/**
 * Bloque de estados de cuenta bancarios: una tarjeta por cuenta con su grid de
 * tres meses, más el contador de avance del encabezado.
 *
 * No es un `UploadCard` con tres archivos: el banco y la CLABE se descubren al
 * subir el primer estado, y el usuario puede declarar varias cuentas.
 */
export const BankStatements = ({ onCompleteChange }: Props) => {
  const [cuentas, setCuentas] = useState<Cuenta[]>([nuevaCuenta(1)]);
  // `id` incremental en vez de Date.now(): dos cuentas agregadas en el mismo ms colisionarían.
  const nextId = useRef(2);

  const totalSubidos = cuentas.reduce((acc, c) => acc + c.archivos.filter(Boolean).length, 0);
  const totalRequerido = cuentas.length * MESES.length;
  const completo = totalRequerido > 0 && totalSubidos === totalRequerido;

  useEffect(() => {
    onCompleteChange(completo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completo]);

  const subirArchivo = (cuentaId: number, idx: number) =>
    setCuentas((cs) =>
      cs.map((c) => {
        if (c.id !== cuentaId) return c;
        const archivos = c.archivos.map((a, i) => (i === idx ? `estado-${idx + 1}.pdf` : a));
        // El banco se detecta con el primer archivo; los siguientes ya no lo reescriben.
        if (c.nombre) return { ...c, archivos };
        return { ...c, archivos, ...BANCO_DETECTADO };
      }),
    );

  const quitarCuenta = (id: number) => setCuentas((cs) => cs.filter((c) => c.id !== id));

  const agregarCuenta = () => {
    setCuentas((cs) => [...cs, nuevaCuenta(nextId.current)]);
    nextId.current += 1;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px" }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: "primary.dark" }}>
          Estados de cuenta bancarios
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: 12,
            color: "text.secondary",
            bgcolor: "#F4F6F9",
            border: "1px solid #E4E7EC",
            borderRadius: 99,
            p: "2px 10px",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: completo ? "success.main" : "text.disabled",
            }}
          />
          {totalSubidos} de {totalRequerido}
        </Box>
      </Box>

      <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.55, mb: "12px" }}>
        Carga los últimos 3 estados de la cuenta donde recibes al menos el <strong>60% de tu facturación</strong>. Si
        recibes ingresos en varias cuentas, sube 3 estados de cada una.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {cuentas.map((cuenta) => (
          <Box
            key={cuenta.id}
            sx={{ border: "1px solid #E4E7EC", borderRadius: "10px", p: "14px 16px", bgcolor: "#F4F6F9" }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "12px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    bgcolor: cuenta.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {cuenta.nombre ? (
                    <Typography sx={{ fontSize: 9, fontWeight: 800, color: "#FFF", letterSpacing: "-0.02em" }}>
                      {cuenta.nombre}
                    </Typography>
                  ) : (
                    <AccountBalanceOutlined sx={{ fontSize: 16, color: "#FFF", opacity: 0.6 }} />
                  )}
                </Box>
                <Box>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: 13.5, color: cuenta.nombre ? "primary.dark" : "text.disabled" }}
                  >
                    {cuenta.nombre ?? "Banco (se detectará al subir)"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.disabled", fontVariantNumeric: "tabular-nums" }}>
                    {cuenta.clabe ? `CLABE ${cuenta.clabe}` : "Sube un estado para identificar la cuenta"}
                  </Typography>
                </Box>
              </Box>

              <Box
                component="button"
                type="button"
                aria-label="Quitar cuenta"
                onClick={() => quitarCuenta(cuenta.id)}
                data-testid={`carga-documentos-quitar-cuenta-${cuenta.id}-button`}
                sx={{
                  all: "unset",
                  cursor: "pointer",
                  color: "text.disabled",
                  p: "4px",
                  display: "inline-flex",
                  "&:hover": { color: "primary.dark" },
                }}
              >
                <Close sx={{ fontSize: 14 }} />
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
              {MESES.map((mes, idx) => {
                const subido = Boolean(cuenta.archivos[idx]);
                return (
                  <Box
                    key={mes}
                    component="button"
                    type="button"
                    onClick={() => subirArchivo(cuenta.id, idx)}
                    data-testid={`carga-documentos-cuenta-${cuenta.id}-mes-${idx + 1}-button`}
                    sx={{
                      all: "unset",
                      boxSizing: "border-box",
                      cursor: "pointer",
                      textAlign: "center",
                      border: "1.5px dashed",
                      borderColor: subido ? "success.main" : "#D6DBE3",
                      borderRadius: "8px",
                      p: "12px 8px",
                      bgcolor: subido ? "#F1FBF4" : "background.paper",
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "primary.dark", mb: "3px" }}>
                      {mes}
                    </Typography>
                    {subido ? (
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          color: "success.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        <Check sx={{ fontSize: 11 }} /> Subido
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 11.5, color: "primary.main" }}>+ Subir PDF</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        component="button"
        type="button"
        onClick={agregarCuenta}
        data-testid="carga-documentos-agregar-cuenta-button"
        sx={{
          all: "unset",
          boxSizing: "border-box",
          mt: "10px",
          cursor: "pointer",
          border: "1px solid #E4E7EC",
          borderRadius: 99,
          p: "7px 16px",
          fontSize: 13,
          color: "primary.dark",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          "&:hover": { bgcolor: "#F4F6F9" },
        }}
      >
        <Add sx={{ fontSize: 13 }} /> Agregar otra cuenta
      </Box>
    </Box>
  );
};
